import { useLocale } from 'next-intl'

import classnames from 'classnames'

import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { TeamMemberCardProps } from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import TeamMemberCard from '~/components/atoms/TeamMemberCard/TeamMemberCard'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { getLocalizedHardCodedSlug } from '~/utils/getLocalizedHardCodedSlug'

import styles from './TeamMembers.module.css'

export interface TeamMembersProps {
  testID?: string
  title?: string
  introText?: string
  buttonText?: string
  members?: TeamMemberCardProps[]
  sectionId?: string
  theme?: ComponentTheme
  moduleId?: string
  center?: boolean
  centralised?: boolean
}

export default function TeamMembers({
  testID,
  title,
  introText,
  buttonText = 'meet the team',
  members,
  theme = ComponentTheme.primary,
  sectionId,
  moduleId,
  center = false,
  centralised = false,
}: TeamMembersProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)
  const locale = useLocale()

  const renderMembers = (members: TeamMemberCardProps[]) => {
    return members.map((item: TeamMemberCardProps, i: number) => {
      return <TeamMemberCard {...item} key={`team-member-item-${i}`} />
    })
  }

  const renderCentredSlider = (members: TeamMemberCardProps[]) => {
    return (
      <>
        {isMobile ? (
          <DynamicSlider
            slidesPerView={1.4}
            slidesPerViewMed={2.4}
            slidesPerViewLrg={4.1}
            overflowVisible={true}
            sectionId={sectionId}
          >
            {members && renderMembers(members)}
          </DynamicSlider>
        ) : (
          <div className={styles.membersGrid}>
            {members && renderMembers(members)}
          </div>
        )}
      </>
    )
  }

  return (
    <div
      className={classnames(
        styles.base,
        styles[theme],
        center && styles.center
      )}
      data-testid={testID}
      id={moduleId}
    >
      <Fixture theme={theme} verticalPadding={true} noPaddingBottom>
        <div style={{ overflow: 'hidden' }}>
          <div className={styles.wrap}>
            <Container cssClass={styles.sliderContainer}>
              <div
                className={classnames({
                  [styles.centralisedTextBlock]: centralised,
                })}
              >
                <Text
                  Tag="h3"
                  size={
                    centralised
                      ? TextSizes.primaryTitleLrgPlus
                      : TextSizes.primaryTitleLrg
                  }
                >
                  {title}
                </Text>
                <Text
                  Tag="p"
                  className={
                    centralised ? styles.centralisedIntro : styles.introText
                  }
                  size={TextSizes.bodyLrg}
                >
                  {introText}
                </Text>
                {!centralised && (
                  <Button
                    iconLeft={true}
                    variant={ButtonVariants.primaryLight}
                    className={styles.button}
                    link={getLocalizedHardCodedSlug('our-people', locale)}
                  >
                    {buttonText}
                  </Button>
                )}
              </div>
              <div className={styles.slider}>
                {center ? (
                  <>{members && renderCentredSlider(members)}</>
                ) : (
                  <DynamicSlider
                    slidesPerView={1.4}
                    slidesPerViewMed={2.4}
                    slidesPerViewLrg={4.1}
                    overflowVisible={true}
                    sectionId={sectionId}
                  >
                    {members && renderMembers(members)}
                  </DynamicSlider>
                )}
              </div>
            </Container>
          </div>
        </div>
      </Fixture>
    </div>
  )
}
